/**
 * Every field of a contact, editable in place.
 *
 * The dialog this replaces edited eight fields, submitted all eight on every
 * save — so a field the person never touched was rewritten and, on a
 * captured row, pinned against capture for good — and offered Type and
 * Organisation on master records that silently ignored them while the
 * response still said "saved". This form diffs the edited values against the
 * record and sends only what changed; a field the record type cannot store
 * is drawn read-only with the reason; and whatever the server could not keep
 * comes back in `ignored` and is shown, not swallowed.
 *
 * Two geometries: `columns` mirrors the contact window's four equal columns
 * (identity, reach, relationship and notes, enrichment); `stack` is one
 * column for the smaller dialog.
 */
import { useState, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { AlertTriangle, Loader2, X } from 'lucide-react'

import {
  CRM_COMPANY_TYPES,
  crmErrorDetail,
  crmService,
  partyTypeFor,
  recordKindLabel,
  type CrmCompanyType,
  type CrmContact,
  type CrmContactPatch,
} from '@/services/crm'
import {
  CRM_STATUS_OPTIONS,
  buildPatch,
  formValuesFor,
  isStorable,
  type ContactFormValues,
  type EnrichmentFormValues,
  type PartyType,
} from './crmFields'

export interface ContactEditFormProps {
  contact: CrmContact
  /** After a successful save: the keys that were sent, and the keys the
   *  record type could not store. An empty patch means nothing changed and
   *  nothing was sent. */
  onSaved: (patch: CrmContactPatch, ignored: string[]) => void
  onCancel: () => void
  layout?: 'columns' | 'stack'
  /** Where "Open existing" goes when the address already belongs to another
   *  contact (a 409). Without it the clash is shown and nothing is offered. */
  onOpenContact?: (id: string) => void
  /** Somewhere else to draw Save and Cancel — the window puts them in its
   *  header. The form keeps owning them so the pending state is in one place. */
  actionsSlot?: HTMLElement | null
}

type StringKey = {
  [K in keyof ContactFormValues]: ContactFormValues[K] extends string ? K : never
}[keyof ContactFormValues]
type ListKey = {
  [K in keyof ContactFormValues]: ContactFormValues[K] extends string[] ? K : never
}[keyof ContactFormValues]
type BoolKey = {
  [K in keyof ContactFormValues]: ContactFormValues[K] extends boolean ? K : never
}[keyof ContactFormValues]
type EnrichStringKey = 'yearsExperience' | 'dealVolume' | 'education'
type EnrichListKey = 'licenses' | 'previousCompanies' | 'specialties'

type PatchKey = keyof CrmContactPatch

const LABEL_CLASS = 'block font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500'
const INPUT_CLASS = 'input input-compact mt-0.5 w-full'
const HINT_CLASS = 'mt-0.5 text-[9px] text-stone-600'

// ── Controls ───────────────────────────────────────────────────────────────

function Field({ id, label, hint, children }: { id: string; label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
      </label>
      {children}
      {hint ? <div className={HINT_CLASS}>{hint}</div> : null}
    </div>
  )
}

/** A value the record type has nowhere to keep: shown as it is, with why. */
function ReadOnlyField({ label, value, kind }: { label: string; value: ReactNode; kind: string }) {
  return (
    <div className="min-w-0">
      <span className={LABEL_CLASS}>{label}</span>
      <div className="mt-0.5 text-[12px] text-stone-400">{value}</div>
      <div className={HINT_CLASS}>Not stored on a {kind}.</div>
    </div>
  )
}

/**
 * A list of values as chips. Enter or a comma adds what is typed; Backspace
 * on an empty box removes the last chip; leaving the box keeps what is typed
 * rather than losing it.
 */
export function ChipsInput({
  id,
  value,
  onChange,
  placeholder,
}: {
  id: string
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string
}) {
  const [draft, setDraft] = useState('')
  const commit = () => {
    const text = draft.trim().replace(/,$/, '').trim()
    setDraft('')
    if (!text || value.includes(text)) return
    onChange([...value, text])
  }
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      commit()
    } else if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }
  return (
    <div className="input input-compact mt-0.5 flex w-full flex-wrap items-center gap-1 !py-0.5">
      {value.map((chip) => (
        <span key={chip} className="inline-flex items-center gap-0.5 rounded border border-amber-400/10 bg-amber-400/[0.06] px-1.5 py-0.5 text-[10px] text-amber-200">
          {chip}
          <button
            type="button"
            onClick={() => onChange(value.filter((v) => v !== chip))}
            aria-label={`Remove ${chip}`}
            className="rounded text-stone-500 hover:text-red-300 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
          >
            <X size={9} />
          </button>
        </span>
      ))}
      <input
        id={id}
        type="text"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={value.length === 0 ? placeholder : undefined}
        className="min-w-[80px] flex-1 bg-transparent text-[11px] text-stone-200 outline-none placeholder:text-stone-600"
      />
    </div>
  )
}

// ── The form ───────────────────────────────────────────────────────────────

interface Ctx {
  partyType: PartyType
  kind: string
  values: ContactFormValues
  initial: ContactFormValues
  set: <K extends keyof ContactFormValues>(key: K, value: ContactFormValues[K]) => void
  setEnrichment: <K extends keyof EnrichmentFormValues>(key: K, value: EnrichmentFormValues[K]) => void
}

/** The patch key a form key is stored under, for the storable check: the
 *  Kind select writes `isEntity`; everything else is named as it is sent. */
function patchKeyFor(key: string): PatchKey {
  if (key === 'kind') return 'isEntity'
  return key as PatchKey
}

function TextInput({
  ctx,
  field,
  label,
  type = 'text',
  hint,
}: {
  ctx: Ctx
  field: StringKey
  label: string
  type?: 'text' | 'email' | 'tel' | 'url' | 'textarea'
  hint?: ReactNode
}) {
  const value = ctx.values[field]
  if (!isStorable(ctx.partyType, patchKeyFor(field))) {
    return value ? <ReadOnlyField label={label} value={value} kind={ctx.kind} /> : null
  }
  const id = `edit-${field}`
  return (
    <Field id={id} label={label} hint={hint}>
      {type === 'textarea' ? (
        <textarea
          id={id}
          value={value}
          onChange={(event) => ctx.set(field, event.target.value)}
          rows={6}
          className={clsx(INPUT_CLASS, 'resize-y leading-relaxed')}
        />
      ) : (
        <input id={id} type={type} value={value} onChange={(event) => ctx.set(field, event.target.value)} className={INPUT_CLASS} />
      )}
    </Field>
  )
}

function NumberInput({ ctx, field, label, hint }: { ctx: Ctx; field: StringKey; label: string; hint?: ReactNode }) {
  const value = ctx.values[field]
  if (!isStorable(ctx.partyType, patchKeyFor(field))) {
    return value ? <ReadOnlyField label={label} value={value} kind={ctx.kind} /> : null
  }
  const id = `edit-${field}`
  return (
    <Field id={id} label={label} hint={hint}>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        step="any"
        value={value}
        onChange={(event) => ctx.set(field, event.target.value)}
        className={INPUT_CLASS}
      />
    </Field>
  )
}

function ListInput({ ctx, field, label, hint, placeholder }: { ctx: Ctx; field: ListKey; label: string; hint?: ReactNode; placeholder?: string }) {
  const value = ctx.values[field]
  if (!isStorable(ctx.partyType, patchKeyFor(field))) {
    return value.length > 0 ? <ReadOnlyField label={label} value={value.join(', ')} kind={ctx.kind} /> : null
  }
  const id = `edit-${field}`
  return (
    <Field id={id} label={label} hint={hint}>
      <ChipsInput id={id} value={value} onChange={(next) => ctx.set(field, next)} placeholder={placeholder} />
    </Field>
  )
}

function CheckInput({ ctx, field, label }: { ctx: Ctx; field: BoolKey; label: string }) {
  const value = ctx.values[field]
  if (!isStorable(ctx.partyType, patchKeyFor(field))) {
    return value ? <ReadOnlyField label={label} value="Yes" kind={ctx.kind} /> : null
  }
  const id = `edit-${field}`
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-[11px] text-stone-300">
      <input
        id={id}
        type="checkbox"
        checked={value}
        onChange={(event) => ctx.set(field, event.target.checked)}
        className="h-3 w-3 accent-amber-400 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
      />
      {label}
    </label>
  )
}

function SelectInput({
  ctx,
  field,
  label,
  options,
  hint,
}: {
  ctx: Ctx
  field: 'companyType' | 'status' | 'kind'
  label: string
  options: readonly string[]
  hint?: ReactNode
}) {
  const current = field === 'kind' ? (ctx.values.isEntity ? 'organisation' : 'person') : ctx.values[field]
  const shown = field === 'kind' ? (ctx.values.isEntity ? 'Organization' : 'Person') : current
  if (!isStorable(ctx.partyType, patchKeyFor(field))) {
    return shown ? <ReadOnlyField label={label} value={shown} kind={ctx.kind} /> : null
  }
  const id = `edit-${field}`
  return (
    <Field id={id} label={label} hint={hint}>
      <select
        id={id}
        value={current}
        onChange={(event) => {
          if (field === 'kind') ctx.set('isEntity', event.target.value === 'organisation')
          else if (field === 'companyType') ctx.set('companyType', event.target.value as CrmCompanyType)
          else ctx.set('status', event.target.value)
        }}
        className={INPUT_CLASS}
      >
        {field === 'kind' ? (
          <>
            <option value="person">Person</option>
            <option value="organisation">Organization</option>
          </>
        ) : (
          options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))
        )}
      </select>
    </Field>
  )
}

function EnrichText({ ctx, field, label, number }: { ctx: Ctx; field: EnrichStringKey; label: string; number?: boolean }) {
  const id = `edit-enrichment-${field}`
  return (
    <Field id={id} label={label}>
      <input
        id={id}
        type={number ? 'number' : 'text'}
        step={number ? 'any' : undefined}
        inputMode={number ? 'decimal' : undefined}
        value={ctx.values.enrichment[field]}
        onChange={(event) => ctx.setEnrichment(field, event.target.value)}
        className={INPUT_CLASS}
      />
    </Field>
  )
}

function EnrichList({ ctx, field, label }: { ctx: Ctx; field: EnrichListKey; label: string }) {
  const id = `edit-enrichment-${field}`
  return (
    <Field id={id} label={label}>
      <ChipsInput id={id} value={ctx.values.enrichment[field]} onChange={(next) => ctx.setEnrichment(field, next)} />
    </Field>
  )
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="min-w-0 space-y-2 border-b border-amber-400/10 pb-3">
      <legend className="mb-1 font-mono text-[10px] uppercase tracking-[0.16em] text-stone-500">{title}</legend>
      {children}
    </fieldset>
  )
}

// ── Groups ─────────────────────────────────────────────────────────────────

function IdentityGroup({ ctx }: { ctx: Ctx }) {
  const sponsor = ctx.partyType === 'sponsor'
  return (
    <Group title="Identity">
      <TextInput ctx={ctx} field="name" label="Name" />
      <TextInput ctx={ctx} field="company" label="Company" />
      {sponsor ? <TextInput ctx={ctx} field="entityType" label="Entity type" /> : <TextInput ctx={ctx} field="title" label="Title" />}
      <SelectInput ctx={ctx} field="companyType" label="Type" options={CRM_COMPANY_TYPES} />
      <SelectInput ctx={ctx} field="kind" label="Kind" options={[]} />
      <CheckInput ctx={ctx} field="isInternal" label="Internal — the tenant’s own staff" />
    </Group>
  )
}

function ReachGroup({ ctx }: { ctx: Ctx }) {
  return (
    <Group title="Reach">
      <TextInput ctx={ctx} field="email" label="Email" type="email" hint={ctx.partyType === 'deal_contact' ? 'Changing the address re-keys this contact; a clash with another contact is refused.' : undefined} />
      <ListInput ctx={ctx} field="emails" label="Other emails" placeholder="Add an address" />
      <TextInput ctx={ctx} field="phone" label="Phone" type="tel" />
      <TextInput ctx={ctx} field="directPhone" label="Direct" type="tel" />
      <TextInput ctx={ctx} field="mobilePhone" label="Mobile" type="tel" />
      <TextInput ctx={ctx} field="officePhone" label="Office" type="tel" />
      <TextInput ctx={ctx} field="address" label="Address" />
      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)] gap-2">
        <TextInput ctx={ctx} field="city" label="City" />
        <TextInput ctx={ctx} field="state" label="State" />
        <TextInput ctx={ctx} field="postalCode" label="Postal code" />
      </div>
      <TextInput ctx={ctx} field="linkedIn" label="LinkedIn" type="url" />
      <TextInput ctx={ctx} field="website" label="Website" type="url" />
    </Group>
  )
}

function RecordGroup({ ctx }: { ctx: Ctx }) {
  const rows: ReactNode[] = []
  if (ctx.partyType === 'deal_contact') {
    rows.push(<TextInput key="roleEvidence" ctx={ctx} field="roleEvidence" label="Role evidence" hint="Why this contact is filed under its type." />)
    rows.push(<TextInput key="companyDomain" ctx={ctx} field="companyDomain" label="Company domain" />)
  }
  if (ctx.partyType === 'broker_firm') {
    rows.push(<ListInput key="emailDomains" ctx={ctx} field="emailDomains" label="Email domains" placeholder="Add a domain" />)
  }
  if (rows.length === 0) return null
  return <Group title="Record">{rows}</Group>
}

function RelationshipGroup({ ctx }: { ctx: Ctx }) {
  if (ctx.partyType === 'sponsor') {
    return (
      <Group title="Sponsor financials">
        <NumberInput ctx={ctx} field="yearsExperience" label="Years of experience" />
        <NumberInput ctx={ctx} field="netWorth" label="Net worth (USD)" />
        <NumberInput ctx={ctx} field="liquidity" label="Liquidity (USD)" />
        <ListInput ctx={ctx} field="flags" label="Flags" placeholder="Add a flag" />
        <ListInput ctx={ctx} field="tags" label="Markets" placeholder="Add a market" />
        <ListInput ctx={ctx} field="assetClasses" label="Asset classes" placeholder="Add an asset class" />
      </Group>
    )
  }
  if (ctx.partyType === 'broker' || ctx.partyType === 'broker_firm') {
    return (
      <Group title="Relationship">
        <SelectInput ctx={ctx} field="status" label="Status" options={CRM_STATUS_OPTIONS} />
        <CheckInput ctx={ctx} field="isPreferred" label="Preferred broker" />
        <CheckInput ctx={ctx} field="isBlocklisted" label="Blocklisted" />
        <ListInput ctx={ctx} field="tags" label="Markets" placeholder="Add a market" />
        {ctx.partyType === 'broker' ? (
          <ListInput ctx={ctx} field="assetClasses" label="Asset classes" placeholder="Add an asset class" />
        ) : null}
      </Group>
    )
  }
  return null
}

function EnrichmentGroup({ ctx }: { ctx: Ctx }) {
  return (
    <Group title="Enriched data">
      <p className="text-[9px] text-stone-600">Set by hand, these are pinned: a later lookup leaves them alone.</p>
      <EnrichText ctx={ctx} field="yearsExperience" label="Years of experience" number />
      <EnrichText ctx={ctx} field="dealVolume" label="Deal volume" />
      <EnrichText ctx={ctx} field="education" label="Education" />
      <EnrichList ctx={ctx} field="licenses" label="Licenses" />
      <EnrichList ctx={ctx} field="previousCompanies" label="Previous companies" />
      <EnrichList ctx={ctx} field="specialties" label="Specialties" />
    </Group>
  )
}

function NotesGroup({ ctx }: { ctx: Ctx }) {
  return (
    <Group title="Notes">
      <TextInput ctx={ctx} field="notes" label="Notes" type="textarea" />
    </Group>
  )
}

// ── Component ──────────────────────────────────────────────────────────────

export default function ContactEditForm({ contact, onSaved, onCancel, layout = 'stack', onOpenContact, actionsSlot }: ContactEditFormProps) {
  const queryClient = useQueryClient()
  const partyType = partyTypeFor(contact.id)
  const kind = recordKindLabel(contact.id)
  // Frozen at mount: a refetch mid-edit must not move the baseline, or a
  // field the person never touched would read as changed and be sent.
  const [initial] = useState<ContactFormValues>(() => formValuesFor(contact))
  const [values, setValues] = useState<ContactFormValues>(initial)
  const [error, setError] = useState<string | null>(null)
  const [clash, setClash] = useState<{ detail: string; existingId: string | null } | null>(null)

  const save = useMutation({
    mutationFn: (patch: CrmContactPatch) => crmService.updateContact(contact.id, patch),
    onSuccess: (result, patch) => {
      queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] })
      queryClient.invalidateQueries({ queryKey: ['crm', 'lists'] })
      queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] })
      queryClient.invalidateQueries({ queryKey: ['crm', 'enrichment'] })
      onSaved(patch, result.ignored ?? [])
    },
    onError: (err) => {
      const response = (err as { response?: { status?: number; data?: { detail?: unknown; existingId?: unknown } } }).response
      if (response?.status === 409) {
        setClash({
          detail: typeof response.data?.detail === 'string' ? response.data.detail : 'Another contact already holds that address.',
          existingId: typeof response.data?.existingId === 'string' ? response.data.existingId : null,
        })
        return
      }
      setError(crmErrorDetail(err, 'The contact could not be saved.'))
    },
  })

  const ctx: Ctx = {
    partyType,
    kind,
    values,
    initial,
    set: (key, value) => setValues((v) => ({ ...v, [key]: value })),
    setEnrichment: (key, value) => setValues((v) => ({ ...v, enrichment: { ...v.enrichment, [key]: value } })),
  }

  const submit = () => {
    if (save.isPending) return
    setError(null)
    setClash(null)
    const patch = buildPatch(initial, values)
    // Nothing changed: nothing is sent. A no-op save must not pin, clear or
    // rewrite anything, and must not log an edit that made no edit.
    if (Object.keys(patch).length === 0) {
      onSaved({}, [])
      return
    }
    save.mutate(patch)
  }

  const actions = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onCancel}
        disabled={save.isPending}
        className="rounded-md border border-amber-400/15 px-2.5 py-1 text-[11px] text-stone-300 hover:border-amber-400/30 hover:text-stone-100 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60 disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={submit}
        disabled={save.isPending}
        className="flex items-center gap-1 rounded-md border border-amber-400/30 bg-amber-400/[0.12] px-2.5 py-1 text-[11px] font-medium text-amber-200 hover:bg-amber-400/[0.2] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {save.isPending ? <Loader2 size={10} className="motion-safe:animate-spin" /> : null}
        {save.isPending ? 'Saving…' : 'Save'}
      </button>
    </div>
  )

  const status = (
    <>
      {clash ? (
        <div role="alert" className="flex items-start gap-2 rounded border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-[11px] text-amber-200">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span className="min-w-0 flex-1">{clash.detail}</span>
          {clash.existingId && onOpenContact ? (
            <button
              type="button"
              onClick={() => onOpenContact(clash.existingId!)}
              className="shrink-0 rounded text-amber-300 underline decoration-dotted underline-offset-2 hover:text-amber-100 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
            >
              Open existing
            </button>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <div role="alert" className="rounded border border-red-400/30 bg-red-400/[0.06] px-3 py-2 text-[11px] text-red-300">
          {error}
        </div>
      ) : null}
    </>
  )

  const column = 'min-h-0 min-w-0 space-y-3 overflow-y-auto pr-1 scrollbar-thin'

  return (
    <form
      id="contact-edit-form"
      className={clsx('flex min-h-0 flex-1 flex-col', layout === 'columns' ? 'p-4' : 'px-4 py-3')}
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
      aria-label={`Edit ${contact.displayName}`}
    >
      {(clash || error) && <div className="mb-3 space-y-2">{status}</div>}
      {layout === 'columns' ? (
        <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-3 xl:grid-cols-4 xl:grid-rows-1">
          <div className={column}>
            <IdentityGroup ctx={ctx} />
            <RecordGroup ctx={ctx} />
          </div>
          <div className={column}>
            <ReachGroup ctx={ctx} />
          </div>
          <div className={column}>
            <RelationshipGroup ctx={ctx} />
            <NotesGroup ctx={ctx} />
          </div>
          <div className={column}>
            <EnrichmentGroup ctx={ctx} />
          </div>
        </div>
      ) : (
        <div className={clsx(column, 'flex-1')}>
          <IdentityGroup ctx={ctx} />
          <ReachGroup ctx={ctx} />
          <RecordGroup ctx={ctx} />
          <RelationshipGroup ctx={ctx} />
          <EnrichmentGroup ctx={ctx} />
          <NotesGroup ctx={ctx} />
        </div>
      )}
      {actionsSlot ? (
        createPortal(actions, actionsSlot)
      ) : (
        <div className="mt-3 flex shrink-0 items-center justify-end border-t border-amber-400/10 pt-3">{actions}</div>
      )}
    </form>
  )
}
